import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type {
  Folder,
  FolderContents,
  FolderDetail,
  FolderMember,
} from "@/lib/api-types";

/**
 * Folder hooks (migration 0012). Backend routes live in worker/routes/editor.js.
 * Unlimited nesting via `parentFolderId`; blanket-read share tokens live on the
 * folder row itself. Restricted-folder ACL enforced INDEPENDENT of project ACL.
 *
 * Query key shape:
 *   ["projects", projectId, "folders", parent]       — level listing
 *   ["folders", folderId]                            — meta + breadcrumb
 *   ["folders", folderId, "contents"]                — folder-detail page payload
 *   ["folders", folderId, "members"]                 — restricted-folder ACL
 */

// ── Level listing ────────────────────────────────────────────────────────────

/**
 * GET /api/editor/projects/:pid/folders?parent=root|<id>
 * Returns direct-child folders at the given level. Use `parent="root"` for the
 * project's top level.
 */
export function useProjectFolders(
  projectId: string | undefined,
  parent: string = "root"
) {
  return useQuery<Folder[]>({
    queryKey: ["projects", projectId, "folders", parent],
    queryFn: () =>
      api.get<Folder[]>(
        `/editor/projects/${projectId}/folders?parent=${encodeURIComponent(parent)}`
      ),
    enabled: !!projectId,
    staleTime: 15_000,
  });
}

/** One node in the sidebar folder tree (GET …/folders/tree). */
export type FolderTreeNode = {
  id: string;
  name: string;
  parentFolderId: string | null;
  restricted: boolean;
};

/**
 * GET /api/editor/projects/:pid/folders/tree — ALL folders in the project, flat.
 * The sidebar nests them by `parentFolderId`. One request for the whole tree.
 */
export function useProjectFolderTree(projectId: string | undefined) {
  return useQuery<FolderTreeNode[]>({
    queryKey: ["projects", projectId, "folder-tree"],
    queryFn: () =>
      api.get<FolderTreeNode[]>(`/editor/projects/${projectId}/folders/tree`),
    enabled: !!projectId,
    staleTime: 30_000,
  });
}

// ── Folder detail ────────────────────────────────────────────────────────────

export function useFolder(folderId: string | undefined) {
  return useQuery<FolderDetail>({
    queryKey: ["folders", folderId],
    queryFn: () => api.get<FolderDetail>(`/editor/folders/${folderId}`),
    enabled: !!folderId,
    staleTime: 15_000,
  });
}

/**
 * GET /api/editor/folders/:id/contents — one payload with subfolders + videos.
 * Preferred over calling useProjectFolders + useProjectVideos separately
 * inside the folder-detail page (saves a round-trip).
 */
export function useFolderContents(folderId: string | undefined) {
  return useQuery<FolderContents>({
    queryKey: ["folders", folderId, "contents"],
    queryFn: () => api.get<FolderContents>(`/editor/folders/${folderId}/contents`),
    enabled: !!folderId,
    staleTime: 15_000,
  });
}

// ── Mutations ────────────────────────────────────────────────────────────────

type CreateFolderVars = {
  projectId: string;
  name: string;
  /** Omit / null = create at project root. */
  parentFolderId?: string | null;
};

export function useCreateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, name, parentFolderId }: CreateFolderVars) =>
      api.post<{ ok: true; id: string }>(`/editor/projects/${projectId}/folders`, {
        name,
        parentFolderId: parentFolderId ?? null,
      }),
    onSuccess: (_data, vars) => {
      const parent = vars.parentFolderId ?? "root";
      qc.invalidateQueries({
        queryKey: ["projects", vars.projectId, "folders", parent],
      });
      // Parent folder's contents cache also needs a refresh when we create a subfolder.
      if (vars.parentFolderId) {
        qc.invalidateQueries({ queryKey: ["folders", vars.parentFolderId, "contents"] });
      }
    },
  });
}

type UpdateFolderVars = {
  folderId: string;
  name?: string;
  restricted?: boolean;
  /** Passed only for cache invalidation of the parent listing — not sent to the API. */
  projectId?: string;
  parentFolderId?: string | null;
};

export function useUpdateFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId, name, restricted }: UpdateFolderVars) => {
      const body: Record<string, unknown> = {};
      if (name !== undefined) body.name = name;
      if (restricted !== undefined) body.restricted = restricted;
      return api.patch<{ ok: true }>(`/editor/folders/${folderId}`, body);
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["folders", vars.folderId] });
      qc.invalidateQueries({ queryKey: ["folders", vars.folderId, "contents"] });
      if (vars.projectId) {
        const parent = vars.parentFolderId ?? "root";
        qc.invalidateQueries({
          queryKey: ["projects", vars.projectId, "folders", parent],
        });
      }
    },
  });
}

type DeleteFolderVars = {
  folderId: string;
  projectId?: string;
  parentFolderId?: string | null;
};

/**
 * DELETE /api/editor/folders/:id — soft-delete + cascade to descendants
 * (matches project trash). 10-day TTL; permanent delete + restore are not yet
 * wired up (Task 6 verification will call them out).
 */
export function useDeleteFolder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ folderId }: DeleteFolderVars) =>
      api.delete<{ ok: true }>(`/editor/folders/${folderId}`),
    onSuccess: (_data, vars) => {
      if (vars.projectId) {
        const parent = vars.parentFolderId ?? "root";
        qc.invalidateQueries({
          queryKey: ["projects", vars.projectId, "folders", parent],
        });
        // Videos inside the folder subtree were cascaded — refresh the project's videos too.
        qc.invalidateQueries({ queryKey: ["projects", vars.projectId, "videos"] });
      }
      if (vars.parentFolderId) {
        qc.invalidateQueries({ queryKey: ["folders", vars.parentFolderId, "contents"] });
      }
      qc.invalidateQueries({ queryKey: ["trash"] });
    },
  });
}

// ── Folder members (restricted-folder ACL) ───────────────────────────────────

export function useFolderMembers(folderId: string | undefined, enabled = true) {
  return useQuery<FolderMember[]>({
    queryKey: ["folders", folderId, "members"],
    queryFn: () => api.get<FolderMember[]>(`/editor/folders/${folderId}/members`),
    enabled: !!folderId && enabled,
    staleTime: 15_000,
  });
}

export function useAddFolderMember(folderId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.post<{ ok: true; alreadyMember?: boolean }>(
        `/editor/folders/${folderId}/members`,
        { userId }
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["folders", folderId, "members"] });
    },
  });
}

export function useRemoveFolderMember(folderId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) =>
      api.delete<{ ok: true }>(`/editor/folders/${folderId}/members/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["folders", folderId, "members"] });
    },
  });
}

// Legacy per-folder share hooks removed 2026-07-18 (Task 14 cleanup).
// Replaced by first-class shares model — see src/hooks/use-share.tsx
// (useCreateShare / useUpdateShare / useDeleteShare / useRotateShare) and
// src/components/shares/share-dialog.tsx (unified share UI).
